import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AgentChatWorkspace,
  AgentChatWorkspaceExhibit,
  BaseAgentChatWorkspace,
  CreateAgentChatWorkspaceExhibit,
  UpdateAgentChatWorkspaceExhibit,
} from '../types/agent-chat-workspace-types';
import { baseQueryWithErrorHandling } from './baseQuery';

// Draft-Proofread Cycle Types
interface DraftProofreadCycleConfig {
  initial_message: string;
  document_id: string;
  thread_id?: string;
  max_iterations: number;
  quality_threshold: number;
  example_demand_letter_id?: string;
  auto_apply_edits?: boolean;
  focus_rules: string[];
}

interface DraftProofreadCycleResponse {
  workflow_id: string;
  status: string;
  message: string;
}

interface DraftProofreadCycleStatus {
  workflow_id: string;
  status: string;
  current_iteration: number;
  max_iterations: number;
  quality_threshold: number;
  current_mode: string;
  critical_issues_count: number;
  is_complete: boolean;
  error_message?: string;
  last_updated: string;
}

interface DraftProofreadCycleOptions {
  workspace: {
    id: string;
    name: string;
    document_id: string;
  };
  threads: Array<{
    id: string;
    title: string;
    created_at: string;
    modified_at: string;
    message_count: number;
  }>;
  suggested_thread_action: 'create_new' | 'select_existing';
  example_demand_letters: Array<{
    id: string;
    title: string;
    created_at: string;
  }>;
  default_options: {
    max_iterations: number;
    quality_threshold: number;
    focus_rules: string[];
  };
}

export const agentChatWorkspaceApiClient = createApi({
  reducerPath: 'agentChatWorkspaceApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'AgentChatWorkspaces',
    'AgentChatWorkspaceExhibits',
    'DraftProofreadCycle',
  ] as const,
  endpoints: (builder) => ({
    listAgentChatWorkspaces: builder.query<
      AgentChatWorkspace[],
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/agent-chat-workspaces/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'AgentChatWorkspaces', id: matterId },
      ],
    }),

    /**
     * Get or create the singleton "Finch Agent Demand" workspace for this matter.
     * This endpoint ensures exactly one demand workspace exists per matter.
     * Used for early exhibit selection before case context generation.
     */
    getSingletonWorkspace: builder.query<
      AgentChatWorkspace,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/singleton/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'AgentChatWorkspaces', id: matterId },
        result
          ? { type: 'AgentChatWorkspaces', id: `${matterId}:${result.id}` }
          : { type: 'AgentChatWorkspaces', id: 'SINGLETON' },
      ],
    }),

    getAgentChatWorkspace: builder.query<
      AgentChatWorkspace,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/`,
      providesTags: (result, error, { matterId, workspaceId }) => [
        { type: 'AgentChatWorkspaces', id: `${matterId}:${workspaceId}` },
      ],
    }),

    createAgentChatWorkspace: builder.mutation<
      AgentChatWorkspace,
      { matterId: string; data: BaseAgentChatWorkspace }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'AgentChatWorkspaces', id: matterId },
      ],
    }),

    updateAgentChatWorkspace: builder.mutation<
      AgentChatWorkspace,
      {
        matterId: string;
        workspaceId: string;
        data: BaseAgentChatWorkspace;
      }
    >({
      query: ({ matterId, workspaceId, data }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        { type: 'AgentChatWorkspaces', id: `${matterId}:${workspaceId}` },
        { type: 'AgentChatWorkspaces', id: matterId },
      ],
    }),

    deleteAgentChatWorkspace: builder.mutation<
      void,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'AgentChatWorkspaces', id: matterId },
      ],
    }),

    // Exhibit endpoints
    listAgentChatWorkspaceExhibits: builder.query<
      AgentChatWorkspaceExhibit[],
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/exhibits/`,
      providesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'AgentChatWorkspaceExhibits',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    getAgentChatWorkspaceExhibit: builder.query<
      AgentChatWorkspaceExhibit,
      { matterId: string; workspaceId: string; exhibitId: string }
    >({
      query: ({ matterId, workspaceId, exhibitId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/exhibits/${exhibitId}/`,
      providesTags: (result, error, { matterId, workspaceId, exhibitId }) => [
        {
          type: 'AgentChatWorkspaceExhibits',
          id: `${matterId}:${workspaceId}:${exhibitId}`,
        },
      ],
    }),

    createAgentChatWorkspaceExhibit: builder.mutation<
      AgentChatWorkspaceExhibit,
      {
        matterId: string;
        workspaceId: string;
        data: CreateAgentChatWorkspaceExhibit;
      }
    >({
      query: ({ matterId, workspaceId, data }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/exhibits/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'AgentChatWorkspaceExhibits',
          id: `${matterId}:${workspaceId}`,
        },
        {
          type: 'AgentChatWorkspaces',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    updateAgentChatWorkspaceExhibit: builder.mutation<
      AgentChatWorkspaceExhibit,
      {
        matterId: string;
        workspaceId: string;
        exhibitId: string;
        data: UpdateAgentChatWorkspaceExhibit;
      }
    >({
      query: ({ matterId, workspaceId, exhibitId, data }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/exhibits/${exhibitId}/`,
        method: 'PUT',
        body: data,
      }),
      async onQueryStarted(
        { matterId, workspaceId, exhibitId, ...patch },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          agentChatWorkspaceApiClient.util.updateQueryData(
            'listAgentChatWorkspaceExhibits',
            { matterId, workspaceId },
            (draft) => {
              const moveTo = patch.data.index;
              const moveFrom = draft.find(
                (exhibit) => exhibit.id === exhibitId,
              )?.order;

              if (typeof moveFrom === 'number' && typeof moveTo === 'number') {
                const [movedItem] = draft.splice(moveFrom, 1);
                movedItem.order = moveTo;
                movedItem.modified_at = new Date().toISOString();
                movedItem.name = patch.data.name ?? movedItem.name;
                draft.splice(moveTo, 0, movedItem);
              }

              draft.forEach((exhibit, index) => {
                exhibit.order = index;
              });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (
        result,
        error,
        { matterId, workspaceId, exhibitId },
      ) => [
        {
          type: 'AgentChatWorkspaceExhibits',
          id: `${matterId}:${workspaceId}`,
        },
        {
          type: 'AgentChatWorkspaceExhibits',
          id: `${matterId}:${workspaceId}:${exhibitId}`,
        },
        {
          type: 'AgentChatWorkspaces',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    deleteAgentChatWorkspaceExhibit: builder.mutation<
      void,
      { matterId: string; workspaceId: string; exhibitId: string }
    >({
      query: ({ matterId, workspaceId, exhibitId }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/exhibits/${exhibitId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'AgentChatWorkspaceExhibits',
          id: `${matterId}:${workspaceId}`,
        },
        {
          type: 'AgentChatWorkspaces',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    // Draft-Proofread Cycle endpoints
    getDraftProofreadCycleOptions: builder.query<
      DraftProofreadCycleOptions,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/draft-proofread-cycle/options/`,
      providesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'DraftProofreadCycle',
          id: `options:${matterId}:${workspaceId}`,
        },
        {
          type: 'AgentChatWorkspaces',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    startDraftProofreadCycle: builder.mutation<
      DraftProofreadCycleResponse,
      {
        matterId: string;
        workspaceId: string;
        config: DraftProofreadCycleConfig;
      }
    >({
      query: ({ matterId, workspaceId, config }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/draft-proofread-cycle/start_cycle/`,
        method: 'POST',
        body: config,
      }),
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'DraftProofreadCycle',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    getDraftProofreadCycleStatus: builder.query<
      DraftProofreadCycleStatus,
      { matterId: string; workspaceId: string; workflowId?: string }
    >({
      query: ({ matterId, workspaceId, workflowId }) => {
        const params = workflowId ? `?workflow_id=${workflowId}` : '';
        return `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/draft-proofread-cycle/status/${params}`;
      },
      providesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'DraftProofreadCycle',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),

    stopDraftProofreadCycle: builder.mutation<
      { message: string },
      { matterId: string; workspaceId: string; workflowId: string }
    >({
      query: ({ matterId, workspaceId, workflowId }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/draft-proofread-cycle/stop_cycle/`,
        method: 'POST',
        body: { workflow_id: workflowId },
      }),
      invalidatesTags: (result, error, { matterId, workspaceId }) => [
        {
          type: 'DraftProofreadCycle',
          id: `${matterId}:${workspaceId}`,
        },
      ],
    }),
  }),
});

export const {
  useListAgentChatWorkspacesQuery,
  useGetAgentChatWorkspaceQuery,
  useGetSingletonWorkspaceQuery,
  useCreateAgentChatWorkspaceMutation,
  useUpdateAgentChatWorkspaceMutation,
  useDeleteAgentChatWorkspaceMutation,
  useListAgentChatWorkspaceExhibitsQuery,
  useGetAgentChatWorkspaceExhibitQuery,
  useCreateAgentChatWorkspaceExhibitMutation,
  useUpdateAgentChatWorkspaceExhibitMutation,
  useDeleteAgentChatWorkspaceExhibitMutation,
  useGetDraftProofreadCycleOptionsQuery,
  useStartDraftProofreadCycleMutation,
  useGetDraftProofreadCycleStatusQuery,
  useStopDraftProofreadCycleMutation,
} = agentChatWorkspaceApiClient;
